import { Button, ChoiceGroup, ValidateControl } from 'com-ui2/controls'
import { own_ecommerce_business, PublicService, PublisherRegister, type_ecommerce_business } from 'src/services'
import {
  isEmail,
  isEqualPassword,
  passwordValidators,
  phoneValidators,
  useCheckMobile,
  useGlobalState
} from 'com-ui2/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Checkbox } from 'primereact/checkbox'
import { InputCountries } from 'com-ui2/controls/InputCountries'
import { InputPhone } from 'com-ui2/controls/InputPhone'
import { Password } from 'primereact/password'
import { regionCode, TimeZone } from 'com-ui2/utilities'
import css from 'styled-jsx/css'
import getConfig from 'next/config'
import { getProfileState } from 'com-ui-biz/profile/stores/store'
import produce from 'immer'
import sha256 from 'crypto-js/sha256'
import useDebounce from 'com-ui/hooks/useDebounce'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { loadReCaptcha, ReCaptcha } from 'react-recaptcha-v3'
import YesBussiness from './YesBussiness'
import NoBussiness from './NoBussiness'
import { CMS_LOCAL_SIGN_UP, pixelData, timeout } from 'src/helpers'
import { useFbp } from 'src/hooks/useFbp'
import { useGtag } from 'src/hooks/useGtag'
import getLogRocket from 'src/components/logrocket/logRocket'
import { useCheckDomain } from 'src/hooks/useCheckDomain'
import AutoLogin from './AutoLogin'
import { BasicLogin, LoginService } from 'com-ui-biz/profile/services'

const optionsBussiness = [
  { label: 'Yes, I do', key: own_ecommerce_business.YES },
  { label: 'I donˈt have yet', key: own_ecommerce_business.NO }
]

const captchaSitekey = getConfig().publicRuntimeConfig.recapchaSiteKey

export const SignUpForm = ({ setShowCondition, conditionId }: { setShowCondition: any; conditionId?: string }) => {
  const profile = useSelector(getProfileState)
  const [loading, setLoading] = useState<boolean>(false)
  const [, setLogoutUrl] = useState('')
  const [agree, setAgree] = useState(false)
  const [tokenCaptcha, setTokenCaptcha] = useState('')
  const [captchaRef, setcaptchaRef] = useState<any>()
  const router = useRouter()
  const initEmail = profile?.email || ''
  const [registerData, setRegisterData] = useGlobalState<PublisherRegister | null>(CMS_LOCAL_SIGN_UP)
  const [state, setState] = useState<PublisherRegister>({
    email: initEmail,
    ...(registerData ? registerData : {})
  } as PublisherRegister)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false)
  const [autoLogin, setAutoLogin] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [emailError, setEmailError] = useState('')
  const debounceEmail = useDebounce(state?.email || initEmail, 300)
  const { fbpTrackViewContentSignupStep1, fbpTrackSubmitApplication, fbpTrackRegistration } = useFbp()
  const { gtagTrackViewContentSignupStep1, gtagTrackSubmitApplication } = useGtag()
  const { logRocket } = getLogRocket()
  const [onlyPhone, setOnlyPhone] = useState('')
  const [urlSelless, setUrlSelless] = useGlobalState('_local_url_selless_')
  const ref = useRef(null)
  const isMobile = useCheckMobile()
  useEffect(() => {
    if (useCheckDomain()) {
      logRocket?.init(true)
    }
    if (!registerData && useCheckDomain()) {
      fbpTrackViewContentSignupStep1()
      gtagTrackViewContentSignupStep1()
    }
    const originUrl = window.location.origin
    setLogoutUrl(`${getConfig().publicRuntimeConfig.apiBasePath}auth/logout?redirect_uri=${originUrl}/login`)
  }, [])

  useEffect(() => {
    loadReCaptcha(captchaSitekey)
  }, [])

  const updateToken = () => {
    captchaRef.execute()
  }

  const verifyCallback = (recaptchaToken: string) => {
    if (!recaptchaToken) return
    setTokenCaptcha(recaptchaToken)
  }

  useEffect(() => {
    if (state?.country && !registerData) {
      setState({ ...state, phone: `${(regionCode as any)[state?.country]?.replace('+', '')}` })
      setOnlyPhone('')
    }
  }, [state?.country])

  useEffect(() => {
    ;(async () => {
      if (debounceEmail) {
        try {
          await PublicService.checkEmail({ email: debounceEmail })
          setEmailError('')
        } catch (error) {
          checkEmailError(error)
        }
      }
    })()
  }, [debounceEmail])

  const onChange = (name: string) => (e: any) => {
    const value = (e?.target as any)?.value ?? e
    setState((state) => ({
      ...state,
      [name]: value
    }))
  }

  useEffect(() => {
    const form = ref?.current as any
    if (!form) {
      return
    }
    const _error = form.querySelector('.error-message')
    if (_error) {
      form.querySelector('.error-control').scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    }
    setError(_error)
  })

  const onSubmit = async (isNext = false) => {
    if (!firstSubmit) {
      setFirstSubmit(true)
      const error = await new Promise((resolve) => {
        setTimeout(() => {
          setError((error) => {
            resolve(error)
            return error
          })
        }, 0)
      })
      if (error) {
        return
      }
    }
    if (isNext) {
      router.push(`/sellers/signup-2${window.location.search}`)
      setRegisterData(state)
      return
    }
    const registerForm: PublisherRegister = {
      ...state,
      password: sha256(state.password).toString(),
      timezone: TimeZone.US,
      location_ref: urlSelless ? urlSelless : encodeURIComponent(window.location.href) || ''
    }
    updateToken()
    setLoading(true)
    try {
      const result = await PublicService.register(
        {
          body: registerForm
        },
        {
          headers: {
            'g-recaptcha-response': tokenCaptcha
          }
        }
      )

      if (useCheckDomain()) {
        gtagTrackSubmitApplication()
        const data = pixelData(state)
        if (data) {
          fbpTrackSubmitApplication(data)
          fbpTrackRegistration(data)
        } else {
          fbpTrackSubmitApplication()
          fbpTrackRegistration()
        }
      }
      if ((result || '').toLocaleLowerCase() == 'active') {
        setAutoLogin(true)
        await submitAutoLogin()
      } else {
        await router.push(`/signup-thankyou${window.location.search}`)
      }
      setRegisterData(null)
      setUrlSelless('')
    } catch (error) {
      checkEmailError(error)
      setLoading(false)
    }
  }

  const submitAutoLogin = async () => {
    try {
      const body: BasicLogin = {
        name: (state.email || '').trim().toLocaleLowerCase(),
        sha256: sha256(state.password).toString()
      }
      await timeout(2000)
      await LoginService.login1({ body })
      // await dispatch(clearAllowedPath())
      window.open(`${getConfig().publicRuntimeConfig.hostname}/sell-pages`, '_self')
      // await router.replace(router.query.return_url?.toString() || '/')
    } catch (error) {
      console.log(error)
      router.push(`/signup-thankyou${window.location.search}`)
    }
  }

  const checkEmailError = (error: any) => {
    const data = error?.response?.data
    if (data?.status == 409) {
      if (registerData) {
        setRegisterData(null)
      }
      setEmailError(`${data?.detail || ''}`)
    }
  }

  useEffect(() => {
    if (agree) {
      const nextState = produce(state, (draft) => {
        draft.condition_id = conditionId || ''
      })
      setState(nextState)
    }
  }, [conditionId])

  const onAgree = (e: any) => {
    const checked = e.checked
    setAgree(checked)
    const nextState = produce(state, (draft) => {
      if (checked) {
        draft.condition_id = conditionId || ''
      } else {
        delete draft.condition_id
      }
    })
    setState(nextState)
  }

  const handleOnChange = (value: string, data: any) => {
    onChange('phone')(value)
    setOnlyPhone(value.substring(data?.dialCode?.length, value?.length))
  }

  return (
    <>
      {useMemo(() => {
        return autoLogin && <AutoLogin></AutoLogin>
      }, [autoLogin])}
      {!registerData && !autoLogin && (
        <div className="signup-form" ref={ref}>
          <h1 className="h6 mb-5" style={{ textAlign: 'center' }}>
            Sign up and get started for free
          </h1>
          {/* <h2 className="subtitle-1 text--primary mb-3"> Create a login</h2> */}
          <div className="row">
            <div className="col-12 mb-3">
              <ValidateControl
                title="Email"
                placeholder="Enter your email"
                value={state?.email ?? ''}
                onChange={onChange('email')}
                require
                validators={[isEmail()]}
                firstSubmit={firstSubmit}
                error={emailError}
                disabled={!!profile?.email}
              />
            </div>
            {!profile?.email && (
              <div className="row-input-responsive">
                <div className="col-12 mb-3 ">
                  <ValidateControl
                    title="Password"
                    placeholder="Enter your password"
                    value={state?.password ?? ''}
                    require={!profile?.email}
                    validators={!profile?.email || state?.password ? passwordValidators : []}
                    firstSubmit={firstSubmit}
                    editor={
                      <Password
                        placeholder="Enter your password"
                        toggleMask
                        value={state?.password ?? ''}
                        onChange={onChange('password')}
                        feedback={false}
                      />
                    }
                  />
                </div>

                <div className="col-12 mb-3 ">
                  <ValidateControl
                    title="Confirm password"
                    placeholder="Confirm your password"
                    value={confirmPassword ?? ''}
                    require={!profile?.email}
                    validators={
                      !profile?.email || state?.password ? [isEqualPassword(state?.password, 'Password')] : []
                    }
                    firstSubmit={firstSubmit}
                    editor={
                      <Password
                        placeholder="Confirm your password"
                        toggleMask
                        value={confirmPassword ?? ''}
                        onChange={(e: any) => setConfirmPassword((e.target as any).value)}
                        feedback={false}
                      />
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* <h2 className="subtitle-1 text--primary my-3"> About you</h2> */}
          <div className="row mt-3 ">
            <div className="row-input-responsive">
              <div className="col-12 mb-3 ">
                <ValidateControl
                  title="First name"
                  placeholder="Enter your fist name"
                  value={state?.first_name ?? ''}
                  onChange={onChange('first_name')}
                  firstSubmit={firstSubmit}
                  require
                />
              </div>
              <div className="col-12 mb-3">
                <ValidateControl
                  title="Last name"
                  placeholder="Enter your last name"
                  value={state?.last_name ?? ''}
                  onChange={onChange('last_name')}
                  firstSubmit={firstSubmit}
                  require
                />
              </div>
            </div>
            <div className="col-12 mb-3 country-choice">
              <ValidateControl
                title="Country"
                placeholder="Select the country you are living in"
                require
                value={state?.country ?? ''}
                onChange={onChange('country')}
                firstSubmit={firstSubmit}
                editor={
                  <InputCountries
                    autoFocus={!isMobile}
                    filterInputAutoFocus={!isMobile}
                    placeholder="Select the country you are living in"
                    value={state?.country ?? ''}
                    onChange={onChange('country')}
                  />
                }
              />
            </div>

            <div className="col-12 mb-3">
              <ValidateControl
                title="Phone number"
                placeholder="Enter your phone number"
                require
                validators={phoneValidators}
                value={onlyPhone ? onlyPhone : state?.phone}
                firstSubmit={firstSubmit}
                editor={
                  <InputPhone
                    enableSearch
                    countryCodeEditable={false}
                    countryCode={(state?.country || 'us').toLocaleLowerCase()}
                    placeholder="Enter your phone number"
                    value={state?.phone ?? ''}
                    //@ts-ignore
                    onChange={handleOnChange}
                  />
                }
              />
            </div>
            {/* <div className="col-12 mb-3">
          <ValidateControl
            title="Address"
            placeholder="Address ..."
            require
            value={state?.address ?? ''}
            onChange={onChange('address')}
            firstSubmit={firstSubmit}
          />
        </div> */}
            {/* <div className="col-6 mb-3">
          <ValidateControl
            title="City"
            placeholder="City ..."
            require
            value={state?.city ?? ''}
            onChange={onChange('city')}
            firstSubmit={firstSubmit}
          />
        </div> */}
            {/* <div className="col-6 mb-3">
          <ValidateControl
            title="Zip code"
            placeholder="Zip code..."
            require
            value={state?.zip ?? ''}
            onChange={onChange('zip')}
            firstSubmit={firstSubmit}
          />
        </div> */}
            <div className="col-12 mb-3 experience-choice ">
              <ValidateControl
                title="Do you own any ecommerce business?"
                require
                value={state?.own_ecommerce_business ?? ''}
                onChange={onChange('own_ecommerce_business')}
                firstSubmit={firstSubmit}
                editor={
                  <div className="col-12 d-flex align-items-center mt-2">
                    <ChoiceGroup
                      onChange={onChange('own_ecommerce_business')}
                      value={state?.own_ecommerce_business ?? ''}
                      options={optionsBussiness}
                    />
                  </div>
                }
              />
            </div>
            {state?.own_ecommerce_business && state?.own_ecommerce_business === own_ecommerce_business.YES && (
              <div className="col-12 mb-3 experience-choice">
                <ValidateControl
                  title="Please select type of your current e-commerce business"
                  require={
                    state?.own_ecommerce_business && state?.own_ecommerce_business === own_ecommerce_business.YES
                  }
                  value={state?.type_ecommerce_business || []}
                  firstSubmit={firstSubmit}
                  editor={
                    <div className="col-12 mt-3 e-commerce-style ">
                      <label className=" d-flex align-items-center cursor-pointer mb-3" style={{ marginRight: '80px' }}>
                        <Checkbox
                          className="mr-2"
                          checked={
                            (state?.type_ecommerce_business || []).includes(type_ecommerce_business.DROP_SHIPPING) ?? ''
                          }
                          onChange={(e) => {
                            if (e.checked) {
                              onChange('type_ecommerce_business')([
                                ...(state?.type_ecommerce_business || []),
                                type_ecommerce_business.DROP_SHIPPING
                              ])
                            } else {
                              onChange('type_ecommerce_business')(
                                (state?.type_ecommerce_business || []).filter(
                                  (x) => x != type_ecommerce_business.DROP_SHIPPING
                                )
                              )
                            }
                          }}
                        />
                        <span>{`Dropshipping`}</span>
                      </label>

                      <label className=" d-flex align-items-center cursor-pointer mb-3" style={{ marginRight: '80px' }}>
                        <Checkbox
                          className="mr-2"
                          checked={(state?.type_ecommerce_business || []).includes(type_ecommerce_business.POD) ?? ''}
                          onChange={(e) => {
                            if (e.checked) {
                              onChange('type_ecommerce_business')([
                                ...(state?.type_ecommerce_business || []),
                                type_ecommerce_business.POD
                              ])
                            } else {
                              onChange('type_ecommerce_business')(
                                (state?.type_ecommerce_business || []).filter((x) => x != type_ecommerce_business.POD)
                              )
                            }
                          }}
                        />
                        <span>{`POD`}</span>
                      </label>

                      <label className=" d-flex align-items-center cursor-pointer mb-3" style={{ marginRight: '80px' }}>
                        <Checkbox
                          className="mr-2"
                          checked={(state?.type_ecommerce_business || []).includes(type_ecommerce_business.BRAND) ?? ''}
                          onChange={(e) => {
                            if (e.checked) {
                              onChange('type_ecommerce_business')([
                                ...(state?.type_ecommerce_business || []),
                                type_ecommerce_business.BRAND
                              ])
                            } else {
                              onChange('type_ecommerce_business')(
                                (state?.type_ecommerce_business || []).filter((x) => x != type_ecommerce_business.BRAND)
                              )
                            }
                          }}
                        />
                        <span>{`Brand`}</span>
                      </label>
                    </div>
                  }
                />
              </div>
            )}
          </div>

          <div className={`body-2 no-select py-3 confirm-checkbox `}>
            <label className="pl-2 d-flex align-items-center cursor-pointer">
              <Checkbox className="mr-2" checked={agree} onChange={onAgree} />
              <span>
                {`I have read, understood and accepted Selless's `}
                <span>
                  <u className="text--secondary cursor-pointer" onClick={() => setShowCondition(true)}>
                    Seller Terms and Conditions
                  </u>
                </span>
              </span>
            </label>
          </div>

          <Button
            type="primary"
            className="mt-4 mb-4"
            width="100%"
            onClick={() => onSubmit(true)}
            disabled={error || !state.condition_id}
            style={{ height: '48px' }}
          >
            Next
          </Button>

          {/* <div className="mt-3 text-center">
        {profile?.email ? (
          <a className="text--gray" href={logoutUrl}>
            Back
          </a>
        ) : (
          <Link href="/login">
            <a className="text--gray"> Back </a>
          </Link>
        )}
      </div> */}

          <style jsx>{style}</style>
        </div>
      )}

      <ReCaptcha
        sitekey={captchaSitekey}
        action="submit"
        verifyCallback={verifyCallback}
        ref={(ref: any) => setcaptchaRef(ref)}
      />

      {!autoLogin && !!registerData && state.own_ecommerce_business === own_ecommerce_business.YES && (
        <YesBussiness onChange={onChange} state={state} onSubmitForm={onSubmit} isLoading={loading} />
      )}
      {!autoLogin && !!registerData && state.own_ecommerce_business === own_ecommerce_business.NO && (
        <NoBussiness onChange={onChange} state={state} onSubmitForm={onSubmit} isLoading={loading} />
      )}
    </>
  )
}

const style = css`
  .signup-form {
    padding: 32px;
    box-shadow: var(--depth-16);
    background-color: var(--white-100);
    border-radius: 16px;
    max-width: 650px;
    margin: auto;
  }

  :global(body .experience-choice .p-inputgroup, body .experience-choice .p-inputgroup:hover, body
      .experience-choice
      .p-inputgroup.focus, body .investment-choice .p-inputgroup, body .investment-choice .p-inputgroup:hover, body
      .investment-choice
      .p-inputgroup.focus) {
    border: none !important;
    box-shadow: none !important;
    transition: none;
  }
  :global(.choice-group) {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
  }

  :global(.experience-choice .p-field-checkbox, .experience-choice .p-field-radiobutton, .investment-choice
      .p-field-checkbox, .investment-choice .p-field-radiobutton) {
    width: 100%;
    font-weight: 500;
    font-size: 13px;
    line-height: 24px;
    color: var(--gray-8);
  }

  :global(.row-input-responsive) {
    width: 100%;
  }
  :global(.p-checkbox .p-checkbox-box) {
    border: 2px solid var(--gray-6);
  }
  @media only screen and (max-width: 768px) {
    :global(.p-dropdown-panel) {
      margin: 0 48px;
      left: 0px !important;
    }
    :global(.p-dropdown-panel .p-dropdown-header) {
      display: none;
    }
    :global(.p-dropdown-clear-icon) {
      display: none;
    }
    :global(.react-tel-input .country-list) {
      width: 250px;
    }
  }
  @media only screen and (min-width: 768px) {
    .e-commerce-style {
      display: flex;
      align-items: center;
    }

    .row-input-responsive {
      display: flex;
      width: 50%;
    }

    :global(.experience-choice .p-field-checkbox, .experience-choice .p-field-radiobutton, .investment-choice
        .p-field-checkbox, .investment-choice .p-field-radiobutton) {
      width: 50%;
      font-weight: 500;
      font-size: 13px;
      line-height: 24px;
      color: var(--gray-8);
    }
  }

  @media only screen and (min-width: 620px) {
    .confirm-checkbox {
      display: flex;
    }
  }
`
